"use client";

import { Fragment, useMemo } from "react";
import { Check, ChevronDown, Cpu } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { fetchData, patch } from "@/utils/api-handler";
import {
    getCommandQuickSelectorModels,
    getLlmProviderLabel,
    groupLlmModelsByProvider,
} from "@/components/command-team/command-agent/utils/command-agent-llm-utils";
import type { CommandAgent } from "@/types/command-agent";

interface CommandComposerModelPickerProps {
    agentId: string | null;
}

export function CommandComposerModelPicker({
    agentId,
}: CommandComposerModelPickerProps) {
    const queryClient = useQueryClient();

    const { data: agent, isLoading: isLoadingAgent } = useProtectedQuery<CommandAgent>({
        queryKey: ["command-agent-composer", agentId],
        queryFn: () => fetchData(`/api/command-team/agents/${agentId}`),
        enabled: Boolean(agentId),
        requiredPermissions: ["VIEW_COMMAND_AGENT"],
    });

    const { data: llmModelsData, isLoading: isLoadingModels } = useProtectedQuery({
        queryKey: ["command-llm-models-composer"],
        queryFn: () => fetchData("/api/command-team/llm-config/models"),
        enabled: Boolean(agentId),
    });

    const updateModelMutation = useProtectedMutation({
        mutationFn: (payload: {
            llm_model: string;
            llm_provider?: string;
            llm_max_tokens?: number;
            llm_temperature?: number;
        }) => patch(`/api/command-team/agents/${agentId}`, payload),
        requiredPermissions: ["UPDATE_COMMAND_AGENT"],
        onSuccess: () => {
            toast.success("Model updated");
        },
        onError: (error: { response?: { data?: { message?: string } } }) => {
            toast.error("Failed to update model", {
                description: error?.response?.data?.message || "An unexpected error occurred",
            });
        },
    });

    const modelOptions = useMemo(
        () => getCommandQuickSelectorModels(llmModelsData),
        [llmModelsData]
    );

    const modelGroups = useMemo(
        () => groupLlmModelsByProvider(modelOptions),
        [modelOptions]
    );

    const selectedModelLabel = useMemo(() => {
        if (!agent?.llm_model) return null;
        const match = modelOptions.find(
            (model) =>
                model.model_key === agent.llm_model || model.model_id === agent.llm_model
        );
        return match?.display_name ?? agent.llm_model;
    }, [agent?.llm_model, modelOptions]);

    const handleModelSelect = (selectedModelKey: string) => {
        if (!agentId || !agent || selectedModelKey === agent.llm_model) return;
        const selectedModel = modelOptions.find(
            (model) => model.model_key === selectedModelKey,
        );
        updateModelMutation.mutate(
            {
                llm_model: selectedModelKey,
                ...(selectedModel?.provider ? { llm_provider: selectedModel.provider } : {}),
                ...(typeof selectedModel?.default_max_tokens === "number"
                    ? { llm_max_tokens: selectedModel.default_max_tokens }
                    : {}),
                ...(typeof selectedModel?.default_temperature === "number"
                    ? { llm_temperature: selectedModel.default_temperature }
                    : {}),
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ["command-agent-composer", agentId] });
                    queryClient.invalidateQueries({ queryKey: ["command-agent", agentId] });
                    queryClient.invalidateQueries({ queryKey: ["command-agent-header", agentId] });
                    queryClient.invalidateQueries({ queryKey: ["command-agents-sidebar"] });
                    queryClient.invalidateQueries({ queryKey: ["command-agents"] });
                },
            }
        );
    };

    if (!agentId) {
        return (
            <span className="inline-flex h-7 max-w-[180px] items-center gap-1 rounded-full border border-border px-2 text-xs font-medium text-muted-foreground">
                <Cpu className="size-3 shrink-0" />
                <span className="truncate">Select model</span>
            </span>
        );
    }

    if (isLoadingAgent || isLoadingModels) {
        return <Skeleton className="h-7 w-[132px] rounded-full" />;
    }

    if (!agent) return null;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className="inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-border px-2 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary sm:max-w-[180px] sm:min-w-0"
                    aria-label={selectedModelLabel ? `Model: ${selectedModelLabel}` : "Choose the AI model"}
                    disabled={updateModelMutation.isPending}
                >
                    <Cpu className="size-3 shrink-0" />
                    <span className="hidden min-w-0 truncate sm:inline">
                        {selectedModelLabel ?? "Select model"}
                    </span>
                    <ChevronDown className="size-3 shrink-0" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                side="top"
                className="app-scrollbar max-h-[min(28rem,var(--radix-dropdown-menu-content-available-height))] w-[240px] overflow-y-auto"
            >
                {modelGroups.length === 0 ? (
                    <DropdownMenuItem disabled>No models available</DropdownMenuItem>
                ) : (
                    modelGroups.map(([provider, models], groupIndex) => (
                        <Fragment key={provider}>
                            {groupIndex > 0 ? <DropdownMenuSeparator /> : null}
                            <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground">
                                {getLlmProviderLabel(provider)}
                            </DropdownMenuLabel>
                            {models.map((model) => {
                                const isSelected =
                                    model.model_key === agent.llm_model ||
                                    model.model_id === agent.llm_model;
                                return (
                                    <DropdownMenuItem
                                        key={`${model.provider}-${model.model_key}-${model.model_id}`}
                                        onClick={() => handleModelSelect(model.model_key)}
                                        className="gap-2"
                                    >
                                        <span className="min-w-0 flex-1 truncate text-[13px]">
                                            {model.display_name}
                                        </span>
                                        {isSelected ? (
                                            <Check className="size-3.5 shrink-0 text-primary" />
                                        ) : null}
                                    </DropdownMenuItem>
                                );
                            })}
                        </Fragment>
                    ))
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
